/**
 * Κλήσεις της σελίδας «Κατάλογος». Ίδια endpoints με το παλιό ProductManagement, συν τα νέα του
 * backend της Φάσης 3 (μαζική αλλαγή, σειρά, απόσυρση). fetch όπως οι υπόλοιπες σελίδες διαχείρισης.
 */
import i18n from '../../i18n';
import { API_BASE_URL } from '../config/api';
import type {
  ApiCategory, ApiOptionGroup, ApiProduct, ApiSizeGroup, ApiStoreIngredient, GroupRef, VatCategory,
} from './catalogModel';
import type { StateFeeRow } from '../../utils/feeForm';

export class CatalogApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'CatalogApiError';
    this.status = status;
    this.code = code;
  }
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api${path}`, {
      method: init.method || 'GET',
      headers: { ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...authHeaders() },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new CatalogApiError(0, i18n.t('catalog:errors.network'), 'NETWORK');
  }
  // Ανανέωση token όπως στο services/api (x-new-token).
  const fresh = res.headers?.get?.('x-new-token');
  if (fresh) localStorage.setItem('xentrix_token', fresh);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
    throw new CatalogApiError(res.status, String(message), data?.code);
  }
  return data as T;
}

const list = <T>(data: unknown, key?: string): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (key && data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) {
    return (data as Record<string, T[]>)[key];
  }
  return [];
};

export const catalogApi = {
  categories: async (storeId: number) => list<ApiCategory>(await request(`/products/stores/${storeId}/categories`), 'categories'),
  products: async (storeId: number) => list<ApiProduct>(await request(`/products/stores/${storeId}/products`), 'products'),
  optionGroups: async (storeId: number) => list<ApiOptionGroup>(await request(`/stores/${storeId}/option-groups`)),
  sizeGroups: async (storeId: number) => list<ApiSizeGroup>(await request(`/stores/${storeId}/size-groups`)),
  ingredients: async (storeId: number) => list<ApiStoreIngredient>(await request(`/stores/${storeId}/store-ingredients`)),
  vatCategories: async () => list<VatCategory>(await request('/fiscal/vat-categories')),
  stateFees: async () => list<StateFeeRow & { title?: string; short_label?: string }>(await request('/fiscal/state-fees')),
  suppliers: async () => list<{ id: number; name: string }>(await request('/warehouse/suppliers'), 'suppliers'),

  productGroups: async (productId: number) => list<ApiOptionGroup>(await request(`/products/${productId}/option-groups`)),
  categoryGroups: async (categoryId: number) => list<ApiOptionGroup>(await request(`/categories/${categoryId}/default-option-groups`)),

  // Άμεσοι διακόπτες της λίστας (έγκριση ιδιοκτήτη 12/10/2026: όπως σήμερα, χωρίς «Αποθήκευση»).
  setAvailability: (productId: number, isAvailable: boolean) =>
    request<{ product: { is_available: boolean } }>(`/products/${productId}/availability`, { method: 'PATCH', body: { is_available: isAvailable } }),
  setVisibility: (productId: number, isHidden: boolean) =>
    request<{ product: { is_hidden: boolean } }>(`/products/${productId}/visibility`, { method: 'PATCH', body: { is_hidden: isHidden } }),
  setSizeVisibility: (sizeId: number, isHidden: boolean) =>
    request<{ size: { is_hidden: boolean } }>(`/products/sizes/${sizeId}/visibility`, { method: 'PATCH', body: { is_hidden: isHidden } }),
  withdraw: (productId: number) =>
    request<{ success: boolean; product?: Partial<ApiProduct> }>(`/products/${productId}/withdraw`, { method: 'POST' }),

  // Με την «Αποθήκευση».
  createCategory: (storeId: number, body: Record<string, unknown>) =>
    request<ApiCategory>(`/products/stores/${storeId}/categories`, { method: 'POST', body }),
  updateCategory: (storeId: number, categoryId: number, body: Record<string, unknown>) =>
    request<ApiCategory>(`/products/stores/${storeId}/categories/${categoryId}`, { method: 'PUT', body }),
  setCategoryGroups: (categoryId: number, ids: number[]) =>
    request(`/categories/${categoryId}/default-option-groups`, { method: 'PUT', body: { option_group_ids: ids } }),
  reorderCategories: (storeId: number, items: Array<{ id: number; parent_id: number | null; display_order: number }>) =>
    request(`/products/stores/${storeId}/categories/reorder`, { method: 'PUT', body: { items } }),
  createProduct: (storeId: number, body: Record<string, unknown>) =>
    request<{ product: ApiProduct }>(`/products/stores/${storeId}/products`, { method: 'POST', body }),
  updateProduct: (productId: number, body: Record<string, unknown>) =>
    request<ApiProduct>(`/products/${productId}`, { method: 'PUT', body }),
  patchProduct: (productId: number, body: Record<string, unknown>) =>
    request(`/products/${productId}`, { method: 'PATCH', body }),
  setKioskRecommended: (productId: number, value: boolean) =>
    request(`/products/${productId}/kiosk-recommended`, { method: 'PATCH', body: { is_kiosk_recommended: value } }),
  linkGroup: (productId: number, groupId: GroupRef, displayOrder: number) =>
    request(`/products/${productId}/option-groups`, { method: 'POST', body: { option_group_id: groupId, display_order: displayOrder } }),
  unlinkGroup: (productId: number, groupId: GroupRef) =>
    request(`/products/${productId}/option-groups/${groupId}`, { method: 'DELETE' }),
  bulk: (storeId: number, productIds: number[], set: { is_available?: boolean; is_hidden?: boolean; category_id?: number }) =>
    request<{ updated: number }>(`/products/stores/${storeId}/bulk`, { method: 'POST', body: { product_ids: productIds, set } }),
  reorderProducts: (storeId: number, categoryId: number, productIds: number[]) =>
    request(`/products/stores/${storeId}/products/reorder`, { method: 'PUT', body: { category_id: categoryId, product_ids: productIds } }),
};

export type CatalogApi = typeof catalogApi;

const GREEK = /[\u0370-\u03ff\u1f00-\u1fff]/;

/** Το μήνυμα του server όταν είναι ελληνικό (ο χειριστής το διαβάζει)· αλλιώς το γενικό. */
export const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message && GREEK.test(err.message) ? err.message : fallback;
